import { getSafeClient } from "@/integrations/supabase/safeClient";
import { PublicProfile, FullProfile, SellerProfile } from "@/types/profiles";

/**
 * Sanitizes a profile to ensure only public data is returned
 * This prevents sensitive data from being exposed to the frontend
 */
function sanitizeProfile(profile: any): PublicProfile {
  // Return only the fields that are safe for public viewing
  return {
    id: profile.id,
    name: profile.name,
    avatar_url: profile.avatar_url,
    bio: profile.bio || null,
    created_at: profile.created_at,
    seller_profile_id: profile.seller_profile_id || null,
  };
}

/**
 * Fetches a public profile by ID 
 * Only returns non-sensitive data safe for public viewing
 */
export async function fetchPublicProfile(userId: string): Promise<PublicProfile | null> {
  try {
    if (!userId) return null;
    
    const supabaseClient = await getSafeClient();
    const { data, error } = await supabaseClient
      .from('profiles')
      .select(`
        id,
        name,
        avatar_url,
        bio,
        created_at,
        seller_profile_id
      `)
      .eq('id', userId)
      .single();
    
    if (error) {
      console.error("Error fetching public profile:", error);
      throw error;
    }
    
    if (!data) return null;
    
    return sanitizeProfile(data);
  } catch (error) {
    console.error("Error in fetchPublicProfile:", error);
    throw error;
  }
}

/**
 * Fetches a full profile with all fields
 * This should only be used for the current authenticated user
 * or by administrators
 */
export async function fetchFullProfile(userId: string, currentUserId: string): Promise<FullProfile | null> {
  try {
    // Only allow access if the requested profile belongs to the current user
    if (!userId || !currentUserId || userId !== currentUserId) {
      console.error("Unauthorized attempt to access full profile");
      // Return a public profile instead of the full profile for security
      return fetchPublicProfile(userId);
    }
    
    const supabaseClient = await getSafeClient();
    const { data, error } = await supabaseClient
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error) {
      console.error("Error fetching full profile:", error);
      throw error;
    }
    
    if (!data) return null;
    
    // Return the full profile data since the user is authorized
    return data as FullProfile;
  } catch (error) {
    console.error("Error in fetchFullProfile:", error);
    throw error;
  }
}

/**
 * Fetches a seller profile by ID
 */
export async function fetchSellerProfile(sellerId: string): Promise<SellerProfile | null> {
  try {
    if (!sellerId) return null;
    
    const supabaseClient = await getSafeClient();
    const { data, error } = await supabaseClient
      .from('seller_profiles')
      .select(`
        id,
        name,
        bio,
        website_url,
        logo_url,
        verified,
        created_at
      `)
      .eq('id', sellerId)
      .single();
    
    if (error) {
      console.error("Error fetching seller profile:", error);
      throw error;
    }
    
    if (!data) return null;
    
    return data as SellerProfile;
  } catch (error) {
    console.error("Error in fetchSellerProfile:", error);
    throw error;
  }
}

/**
 * Updates a user's profile
 * Only allows updating fields that the user should be able to modify
 */
export async function updateProfile(
  userId: string, 
  profileData: Partial<PublicProfile>
): Promise<PublicProfile | null> {
  try {
    // Only allow specific fields to be updated
    const allowedFields = ['name', 'avatar_url', 'bio'];
    
    // Filter out any fields that shouldn't be updated
    const sanitizedData = Object.keys(profileData)
      .filter(key => allowedFields.includes(key))
      .reduce((obj, key) => {
        obj[key] = profileData[key as keyof Partial<PublicProfile>];
        return obj;
      }, {} as Record<string, any>);
    
    const supabaseClient = await getSafeClient();
    const { data, error } = await supabaseClient
      .from('profiles')
      .update(sanitizedData)
      .eq('id', userId)
      .select(`
        id,
        name,
        avatar_url,
        bio,
        created_at,
        seller_profile_id
      `)
      .single();
    
    if (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
    
    if (!data) return null;
    
    return sanitizeProfile(data);
  } catch (error) {
    console.error("Error in updateProfile:", error);
    throw error;
  }
}
